import TalkToCarrotTilePage from "components/talk-to-carrot/TalkToCarrotTilePage"
import { getTilePageById } from "lib/contentful/contentfulClient"
import { TilePageRoutes } from "lib/contentful/components/TilePageRoutes"
import React, { ReactElement, ReactNode, useContext, useEffect, useState } from "react"
import { Redirect, Route, Switch, useHistory, useParams } from "react-router-dom"
import { TileOrLanguageActions } from "components/dynamic-content/util/dynamicContent"
import { Paths } from "utils/Paths"
import { TalkToCarrotContext } from "./TalkToCarrotContext"
import { MasterLayout } from "components/views/atoms/layouts/MasterLayout"
import BreadcrumbsHeader from "../views/atoms/BreadcrumbsHeader"
import Breadcrumb from "../views/atoms/Breadcrumb"
import { RootBreadcrumb, SmallHeaderOverride } from "./util/talkToCarrotMolecules"
import { useSelector } from "react-redux"
import { getEmployeeId, getPreferredLocale } from "../../reducers/userInfo"
import { TTCTilePageElement } from "lib/contentful/components/TTCTilePageElement"
import { TalkToCarrotLegal } from "./TalkToCarrotLegal"
import { useIntl } from "react-intl"

const TalkToCarrotLocationRouter = (): JSX.Element => {
  const { locationSlug } = useParams<{ locationSlug: string }>()
  const { talkToCarrotLocationOptions } = useContext(TalkToCarrotContext)
  const history = useHistory()
  const employeeId = useSelector(getEmployeeId)
  const preferredLocale = useSelector(getPreferredLocale)

  const locationOption = talkToCarrotLocationOptions.find(({ slug }) => slug === locationSlug.toLowerCase())
  const { pageId, slug, value } = locationOption || {}
  const baseUrl = `${Paths.TALK_TO_CARROT}/${slug}`

  interface LocationPageStateProps {
    routes: ReactElement
    landingPage: ReactElement<typeof TalkToCarrotTilePage>
  }

  const [landingPageProps, setLandingPageProps] = useState<LocationPageStateProps>(null)

  const actions: TileOrLanguageActions = {
    gotoPageAction: (url) => {
      history.push(url)
    },
    openCalendlyAction: (url) => {
      // Adapted from the "Pop-up text" JavaScript snippet Calendly generates for us
      // https://help.calendly.com/hc/en-us/articles/223147027-Embed-options-overview#1
      window.Calendly.initPopupWidget({
        url: url,
        utm: {
          utmCampaign: employeeId
        }
      })
      return false
    }
  }

  type WrapperProps = {
    children: ReactNode
    breadcrumbs: ReactElement<typeof Breadcrumb>[]
  }

  const Wrapper = ({ children, breadcrumbs }: WrapperProps): JSX.Element => (
    <MasterLayout footer={<TalkToCarrotLegal />} header={<BreadcrumbsHeader breadcrumbs={breadcrumbs} />}>
      <SmallHeaderOverride>{children}</SmallHeaderOverride>
    </MasterLayout>
  )

  const LocationBreadcrumb = ({ isActive = false }: { isActive?: boolean }): JSX.Element => {
    const intl = useIntl()
    return (
      <Breadcrumb
        title={intl.formatDisplayName(value, { type: "region" })}
        isActive={isActive}
        onClick={(): void => history.push(baseUrl)}
      />
    )
  }

  useEffect(() => {
    const loadRouter = async (): Promise<void> => {
      const locationLandingPage = await getTilePageById(pageId, preferredLocale)
      const routes = (
        <TilePageRoutes
          page={locationLandingPage}
          actions={actions}
          baseUrl={baseUrl}
          breadcrumbs={[<RootBreadcrumb key="root" />, <LocationBreadcrumb key="location" />]}
          Wrapper={Wrapper}
        />
      )
      const landingPage = (
        <Wrapper breadcrumbs={[<RootBreadcrumb key="root" />, <LocationBreadcrumb isActive key="location" />]}>
          <TTCTilePageElement page={locationLandingPage} actions={actions} baseUrl={baseUrl} />
        </Wrapper>
      )
      setLandingPageProps({ routes, landingPage })
    }

    if (locationOption && !landingPageProps) {
      loadRouter()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const PageRouting = (): JSX.Element => {
    if (landingPageProps) {
      const { routes, landingPage } = landingPageProps
      return (
        <Switch>
          <Route exact path={baseUrl} render={(): JSX.Element => landingPage} />
          <Route render={(): JSX.Element => routes} />
        </Switch>
      )
    }
    return null
  }

  return <>{locationOption ? <PageRouting /> : <Redirect from="/talk-to-carrot" to="/talk-to-carrot" />}</>
}

export default TalkToCarrotLocationRouter
